import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Edit, Close, Delete, EventBusy } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import {
  holidaysService,
  HolidayRow,
  HolidayScope,
} from "../services/holidaysService";

const SCOPE_LABELS: Record<HolidayScope, string> = {
  national: "Nacional",
  state: "Estadual",
  municipal: "Municipal",
  custom: "Personalizado",
};

const WEEKDAYS = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

// day chega como yyyy-MM-dd puro; montar por partes evita o deslocamento de fuso
// que new Date("yyyy-MM-dd") causa (interpreta como UTC).
const dayParts = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return { y, m, d };
};

const formatDay = (day: string) => {
  const { y, m, d } = dayParts(day);
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const weekdayOf = (day: string) => {
  const { y, m, d } = dayParts(day);
  return WEEKDAYS[new Date(y, m - 1, d).getDay()];
};

interface FormState {
  id: string | null;
  day: string;
  holidayTitle: string;
  scope: HolidayScope;
}

// Botão de ação da linha no padrão do sistema (32px no desktop, 44px no celular pela classe).
const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

const Holidays: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";

  const [rows, setRows] = useState<HolidayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const anoCorrente = String(new Date().getFullYear());
  const [year, setYear] = useState<string>(anoCorrente);
  const [scopeFilter, setScopeFilter] = useState<string>("all");

  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HolidayRow | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      setRows(await holidaysService.getByEntity(entityId, true));
    } catch {
      showToast("Erro ao carregar os feriados.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Feriados";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const years = useMemo(() => {
    const set = new Set<string>(rows.map((r) => r.day.slice(0, 4)));
    set.add(String(new Date().getFullYear()));
    return Array.from(set).sort();
  }, [rows]);

  const clearFilters = () => {
    setYear(anoCorrente);
    setScopeFilter("all");
  };

  const hasFilterChanges = year !== anoCorrente || scopeFilter !== "all";

  const filtered = useMemo(
    () =>
      rows
        .filter((r) => year === "all" || r.day.slice(0, 4) === year)
        .filter((r) => scopeFilter === "all" || r.scope === scopeFilter)
        .sort((a, b) => a.day.localeCompare(b.day)),
    [rows, year, scopeFilter]
  );

  const openCreate = () =>
    setForm({ id: null, day: "", holidayTitle: "", scope: "custom" });

  const openEdit = (row: HolidayRow) =>
    setForm({ id: row.id, day: row.day, holidayTitle: row.holidayTitle, scope: row.scope });

  const save = async () => {
    if (!form) return;
    if (!form.day) {
      showToast("Informe a data do feriado.", "error");
      return;
    }
    if (!form.holidayTitle.trim()) {
      showToast("Informe o nome do feriado.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        day: form.day,
        holidayTitle: form.holidayTitle.trim(),
        scope: form.scope,
      };
      if (form.id) {
        await holidaysService.update(form.id, payload);
        showToast("Feriado atualizado.", "success");
      } else {
        await holidaysService.create(entityId, payload);
        showToast("Feriado incluído.", "success");
      }
      setForm(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar o feriado.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: HolidayRow) => {
    try {
      await holidaysService.setActive(row.id, !row.active);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao alterar o feriado.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await holidaysService.remove(deleteTarget.id);
      showToast("Feriado excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao excluir o feriado.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Feriados
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Calendário da entidade: nacionais carregados pelo produto; estaduais, municipais e recessos cadastrados aqui. Datas ativas geram aviso no agendamento.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Ano</InputLabel>
                  <Select label="Ano" value={year} onChange={(e) => setYear(e.target.value)} notched displayEmpty>
                    <MenuItem value="all">Todos</MenuItem>
                    {years.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Abrangência</InputLabel>
                  <Select label="Abrangência" value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value as HolidayScope | "all")} notched displayEmpty>
                    <MenuItem value="all">Todas</MenuItem>
                    {(Object.keys(SCOPE_LABELS) as HolidayScope[]).map((s) => (
                      <MenuItem key={s} value={s}>{SCOPE_LABELS[s]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Novo feriado" />
              </div>
            </div>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <EventBusy sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum feriado no filtro selecionado</Typography>
              <Typography variant="body2">Ajuste o ano/abrangência ou cadastre um novo feriado.</Typography>
            </Box>
          ) : (
            <div>
              <ResultCount shown={filtered.length} total={rows.length} />
              {/* Cabeçalho: toda lista tem um, e cada dado fica na sua coluna */}
              <Box className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Data</Box>
                <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 90px" }}>Dia</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "2 1 180px", minWidth: 0 }}>Feriado</Box>
                <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 150px" }}>Origem</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Situação</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 80px" }}>Ações</Box>
              </Box>

              {filtered.map((row) => (
                <Box
                  key={row.id}
                  className="c4u-table__row"
                  sx={{ opacity: row.active ? 1 : 0.65, cursor: row.seeded ? "default" : "pointer" }}
                >
                  <Typography className="admin-plans-cell" data-label="Data" sx={{ flex: "0 0 100px", fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary }}>
                    {formatDay(row.day)}
                  </Typography>
                  <Typography className="admin-plans-cell c4u-col--lg" data-label="Dia" sx={{ flex: "0 0 90px", fontSize: "0.85rem", color: colors.textSecondary }}>
                    {weekdayOf(row.day)}
                  </Typography>
                  <Typography className="admin-plans-cell" data-label="Feriado" sx={{ flex: "2 1 180px", minWidth: 0, fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary, wordBreak: "break-word" }}>
                    {row.holidayTitle}
                  </Typography>
                  <Typography className="admin-plans-cell c4u-col--md" data-label="Origem" sx={{ flex: "0 0 150px", fontSize: "0.85rem", color: colors.textSecondary }}>
                    {SCOPE_LABELS[row.scope]}
                    {row.seeded ? " · Produto" : " · Clínica"}
                  </Typography>
                  <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 90px" }}>
                    <Chip
                      label={row.active ? "Ativo" : "Inativo"}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggleActive(row); }}
                      title={row.active ? "Clique para inativar (deixa de avisar na agenda)" : "Clique para ativar"}
                      sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.active ? colors.success : colors.textSecondary }}
                    />
                  </Box>
                  <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 120px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    {!row.seeded && (
                      <>
                        <IconButton
                          className="c4u-row-action"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); openEdit(row); }}
                          title="Editar feriado"
                          sx={rowActionSx(colors.primary, colors.primaryHover)}
                        >
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        <IconButton
                          className="c4u-row-action"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
                          title="Excluir feriado"
                          sx={rowActionSx("#dc3545", "#c82333")}
                        >
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </div>
          )}
          </div>
        </Container>
      </Box>

      {/* Inclusão/edição de feriado da clínica (linhas do seed só ativam/inativam pela lista) */}
      <Dialog open={form !== null} onClose={() => setForm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar feriado" : "Novo feriado"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Data"
                type="date"
                value={form.day}
                onChange={(e) => setForm({ ...form, day: e.target.value })}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Nome do feriado"
                value={form.holidayTitle}
                onChange={(e) => setForm({ ...form, holidayTitle: e.target.value })}
                fullWidth
                size="small"
                inputProps={{ maxLength: 255 }}
                InputLabelProps={{ shrink: true }}
                placeholder="Ex.: Aniversário da cidade, Recesso de fim de ano"
              />
              <FormControl size="small" fullWidth>
                <InputLabel shrink>Abrangência</InputLabel>
                <Select
                  label="Abrangência"
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value as HolidayScope })}
                  notched
                >
                  <MenuItem value="state">Estadual</MenuItem>
                  <MenuItem value="municipal">Municipal</MenuItem>
                  <MenuItem value="custom">Personalizado (recesso, data da clínica)</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                O feriado nasce ativo e passa a gerar aviso ao agendar nessa data. Vale apenas para a sua entidade.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setForm(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Incluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir feriado"
        message={`Excluir "${deleteTarget?.holidayTitle ?? ""}" de ${deleteTarget ? formatDay(deleteTarget.day) : ""}? A data deixa de gerar aviso no agendamento.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default Holidays;
